import { TrendingUp, Users, Award } from "lucide-react";
import { Button } from "../ui/Button";
import { Card } from "../ui/Card";
import { IconBox } from "../ui/IconBox";
import BgImg from "../../assets/images/gallery/9.webp";

const Hero = () => {
  const GOOGLE_FORM_URL = "https://forms.gle/cze2oFduemkLYpbb9";
  const TICKET_URL = "https://nexthappen.com/event/49/aqt-direct-investor-startup-pitch-night";

  const features = [
    {
      icon: TrendingUp,
      title: "Funding*",
      description: "Connect with angels & VCs",
    },
    {
      icon: Users,
      title: "1:1 Meetings",
      description: "Private investor sessions",
    },
    {
      icon: Award,
      title: "Pitch Day",
      description: "Pitch on stage + feedback",
    },
  ];

  const openGoogleForm = () => {
    window.open(GOOGLE_FORM_URL, "_blank", "noopener,noreferrer");
  };

  const openTicketPage = () => {
    window.open(TICKET_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <section
      className="
        relative 
        min-h-[100vh] 
        pt-[10px]   /* Navbar height compensation */
        flex 
        items-center 
        px-6 
        bg-primary 
        text-white 
        overflow-hidden
      "
    >
      {/* ================= BACKGROUND ================= */}
      <div className="absolute inset-0 z-0">
        <img
          src={BgImg}
          alt="Pitch Event Background"
          className="w-full h-full object-cover scale-105"
        />

        {/* DARK GRADIENT OVERLAY (Improves text clarity) */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black/80" />
      </div>

      {/* ================= CONTENT ================= */}
      <div className="relative z-10 w-full max-w-7xl mx-auto py-2">
        <div className="flex flex-col lg:flex-row gap-12 items-start">
          
          {/* LEFT CONTENT */}
          <div className="flex-1 max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
              <span className="block">Pitch Your Startup.</span>
              <span className="block">Meet the Investors.</span>

              <span className="block text-bluePrimary">Get Funded.</span>
            </h1>

            <p className="text-lg md:text-xl text-white/85 mb-8 max-w-2xl">
              Join <span className="font-semibold text-white">AQT Direct</span> —
              a founder-first pitch event connecting ambitious startups with
              active investors, mentors, and media exposure.
            </p>

            {/* BUTTONS CONTAINER */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button
                onClick={openGoogleForm}
                className="
                  bg-gradient-to-r from-bluePrimary to-blueHover
                  hover:from-blueHover hover:to-blue-700
                  text-white px-10 py-4 rounded-xl
                  shadow-xl hover:shadow-2xl
                  transition-all duration-300
                  hover:-translate-y-1
                  text-lg font-semibold
                  w-full sm:w-auto
                "
              >
                Nominate Now
              </Button>
              
              <Button
                onClick={openTicketPage}
                variant="outline"
                className="
                  bg-transparent
                  border-2 border-white
                  hover:bg-white/10
                  text-white px-10 py-4 rounded-xl
                  shadow-xl hover:shadow-2xl
                  transition-all duration-300
                  hover:-translate-y-1
                  text-lg font-semibold
                  w-full sm:w-auto
                "
              >
                Reserve Your Seat
              </Button>
            </div>

            
          </div>

          {/* RIGHT INFO CARD */}
          <div className="w-full lg:w-80">
            <div className="
              bg-black/40 
              backdrop-blur-md 
              border border-white/20 
              rounded-2xl 
              p-6 
              shadow-2xl
            ">
              {/* DATE */}
              <InfoRow
                label="Event Date"
                value="31 January 2026"
                color="text-bluePrimary"
              />

              {/* TIME */}
              <InfoRow
                label="Time"
                value="6:00 PM – 10:00 PM"
                color="text-green-400"
              />

              {/* VENUE */}
              <InfoRow
                label="Place"
                value="Holiday Inn, Gurugram"
                color="text-goldPrimary"
                multiline
              />
            </div>
          </div>
        </div>

        {/* FEATURES */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl">
          {features.map((feature, index) => (
            <Card
              key={index}
              className="
                p-6 rounded-2xl
                bg-white/90
                border border-white/20
                shadow-card
                hover:shadow-xl
                transition-all
                hover:-translate-y-1
              "
              hover
            >
              <div className="flex items-start gap-4">
                <IconBox
                  icon={feature.icon}
                  size="sm"
                  className="rounded-xl bg-blueTint text-bluePrimary"
                />
                <div>
                  <h3 className="text-lg font-bold text-black">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-black/70">
                    {feature.description}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ================= SMALL REUSABLE INFO ROW ================= */
const InfoRow = ({
  label,
  value,
  color,
  multiline = false,
}: {
  label: string;
  value: string;
  color: string;
  multiline?: boolean;
}) => (
  <div className="mb-5 last:mb-0">
    <div className={`text-xs uppercase tracking-wider font-semibold ${color}`}>
      {label}
    </div>
    <div className="text-lg font-bold text-white leading-snug">
      {multiline ? value : value}
    </div>
  </div>
);

export default Hero;